import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { isNullOrUndefined } from '@sla-ng/core';
import { Moment } from 'moment';
import { Observable } from 'rxjs';
import { ICost } from '../models/cost.model';
import { ICostingLevelArticleNr } from '../models/costing-level-article.model';
import { IMovingAveragePrice } from '../models/moving-average-price.model';
import { IPlannedSalePrice } from '../models/planned-sale-price.model';
import { IProductionOrder } from '../models/production-order.model';
import { IProductionVersion } from '../models/production-version.model';
import { IPurchase } from '../models/purchase.model';
import { IRecipe } from '../models/recipe.model';
import { ISale } from '../models/sale.model';
import { ITimeRecord } from '../models/time-record.model';
import { IWorkingPlan } from '../models/working-plan.model';
import { CostAccountingRequestService } from '../services/cost-accounting-request.request.service';

const BASE_URL: string = 'transactional';
const DATEFORMAT: string = 'YYYY-MM-DD';

@Injectable({
    providedIn: 'root',
})
export class TransactionalDataResource {
    constructor(private myCostAccountingRequestService: CostAccountingRequestService) {
    }

    public getPurchases(startDate?: Moment, endDate?: Moment): Observable<IPurchase[]> {
        let params: Params = {};
        if (startDate && endDate) {
            params = { startDate: startDate.format(DATEFORMAT), endDate: endDate.format(DATEFORMAT) };
        }
        return this.myCostAccountingRequestService.get<IPurchase[]>(`${BASE_URL}/purchase`, { params });
    }

    public getSales(startDate?: Moment, endDate?: Moment): Observable<ISale[]> {
        let params: Params = {};
        if (startDate && endDate) {
            params = { startDate: startDate.format(DATEFORMAT), endDate: endDate.format(DATEFORMAT) };
        }
        return this.myCostAccountingRequestService.get<ISale[]>(`${BASE_URL}/sale`, { params });
    }

    public getMovingAveragePrices(startDate?: Moment, endDate?: Moment): Observable<IMovingAveragePrice[]> {
        let params: Params = {};
        if (startDate && endDate) {
            params = { startDate: startDate.format(DATEFORMAT), endDate: endDate.format(DATEFORMAT) };
        }
        return this.myCostAccountingRequestService.get<IMovingAveragePrice[]>(`${BASE_URL}/moving-average-price`, { params });
    }

    public getCosts(startDate?: Moment, endDate?: Moment): Observable<ICost[]> {
        let params: Params = {};
        if (startDate && endDate) {
            params = { startDate: startDate.format(DATEFORMAT), endDate: endDate.format(DATEFORMAT) };
        }
        return this.myCostAccountingRequestService.get<ICost[]>(`${BASE_URL}/cost`, { params });
    }

    public getProductionVersions(onlyEndProducts: boolean, onlySemiProducts: boolean, articleNr: string): Observable<IProductionVersion[]> {
        const params: Params = { onlyEndProducts, onlySemiProducts };
        if (!isNullOrUndefined(articleNr)) {
            params[ 'articleNr' ] = articleNr;
        }
        return this.myCostAccountingRequestService.get<IProductionVersion[]>(`${BASE_URL}/production-version`, { params });
    }

    public getRecipe(productionVersionId: number): Observable<IRecipe> {
        return this.myCostAccountingRequestService.get<IRecipe>(`${BASE_URL}/production-version/${productionVersionId}/recipe`);
    }

    public getTimeRecords(startDate?: Moment, endDate?: Moment): Observable<ITimeRecord[]> {
        let params: Params = {};
        if (startDate && endDate) {
            params = { startDate: startDate.format(DATEFORMAT), endDate: endDate.format(DATEFORMAT) };
        }
        return this.myCostAccountingRequestService.get<ITimeRecord[]>(`${BASE_URL}/time-record`, { params });
    }

    public getProductionOrders(startDate?: Moment, endDate?: Moment): Observable<IProductionOrder[]> {
        let params: Params = {};
        if (startDate && endDate) {
            params = { startDate: startDate.format(DATEFORMAT), endDate: endDate.format(DATEFORMAT) };
        }
        return this.myCostAccountingRequestService.get<IProductionOrder[]>(`${BASE_URL}/production`, { params });
    }

    public getProductionOrder(productionOrderNr): Observable<IProductionOrder> {
        return this.myCostAccountingRequestService.get<IProductionOrder>(`${BASE_URL}/production/${productionOrderNr}`);
    }

    public getPlannedSalePrice(startDate?: Moment, endDate?: Moment): Observable<IPlannedSalePrice[]> {
        let params: Params = {};
        if (startDate && endDate) {
            params = { startDate: startDate.format(DATEFORMAT), endDate: endDate.format(DATEFORMAT) };
        }
        return this.myCostAccountingRequestService.get<IPlannedSalePrice[]>(`${BASE_URL}/planned-sale-price`, { params });
    }

    public getCostingLevelArticleMapping(costingLevelId?: number): Observable<ICostingLevelArticleNr[]> {
        let params: Params = {};
        if (costingLevelId) {
            params = { costingLevelId };
        }
        return this.myCostAccountingRequestService.get<ICostingLevelArticleNr[]>(`${BASE_URL}/costing-level-article`, { params });
    }

    public getWorkingPlans(articleNr?: string): Observable<IWorkingPlan[]> {
        let params: Params = {};
        if (articleNr) {
            params = { articleNr };
        }
        return this.myCostAccountingRequestService.get<IWorkingPlan[]>(`${BASE_URL}/working-plan`, { params });
    }
}
